import { useCallback, useEffect, useMemo, useState } from 'react';

import { FaPlus, FaWallet } from 'react-icons/fa6';
import { HiOutlineCalendarDays, HiOutlineCurrencyRupee } from 'react-icons/hi2';
import { IoSearchSharp } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import HOC from '../../components/HOC/HOC';
import styles from '../../css/style.module.css';

import img from '../../assest/loading1.gif';
import { getApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import { formatDateTimeP } from '../../utils/utils';

import Pagination from '../../components/Pagination/Pagination';
import StaffWalletDateFilters from './StaffWalletDateFilters';
import pageStyles from './StaffWalletManagement.module.css';
import { formatCurrency, getPresetDateRange } from './staffWalletDateUtils';

const TYPE_OPTIONS = [
  { label: 'All Types', value: '' },
  { label: 'Credit', value: 'Credit' },
  { label: 'Debit / Withdrawal', value: 'Withdrawal' },
  { label: 'Other', value: 'Other' },
];

const StaffWalletManagement = () => {
  const navigate = useNavigate();
  const [walletdata, setWalletData] = useState(null);
  const [dueSummary, setDueSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [activePreset, setActivePreset] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');
  const [appliedType, setAppliedType] = useState('');
  const [pagination, setPagination] = useState({
    limit: 10,
    totalPages: 1,
    page: 1,
    hasPrevPage: false,
    hasNextPage: false,
  });

  const hasActiveDateFilter = Boolean(appliedFromDate && appliedToDate);

  const fetchData = useCallback(async () => {
    await getApi(
      endPoints.getAllStaffWalletTransactions(
        pagination.page,
        pagination.limit,
        search,
        appliedFromDate,
        appliedToDate,
        appliedType
      ),
      {
        setResponse: setWalletData,
        setLoading,
        errorMsg: 'Failed to fetch wallet transactions!',
        onError: () => setWalletData(null),
      }
    );
  }, [pagination.page, pagination.limit, search, appliedFromDate, appliedToDate, appliedType]);

  const fetchDueSummary = useCallback(async () => {
    await getApi(endPoints.getStaffDueSummary(search), {
      setLoading: setSummaryLoading,
      setResponse: response => {
        setDueSummary(response?.summary ?? {});
      },
      errorMsg: 'Failed to fetch staff due summary!',
      onError: () => setDueSummary(null),
    });
  }, [search]);

  useEffect(() => {
    fetchDueSummary();
  }, [fetchDueSummary]);

  useEffect(() => {
    setPagination(prevPagination => ({
      ...prevPagination,
      totalPages: walletdata?.data?.totalPages ?? 1,
      hasPrevPage: walletdata?.data?.hasPrevPage ?? false,
      hasNextPage: walletdata?.data?.hasNextPage ?? false,
    }));
  }, [walletdata]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = e => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const applyDateRange = (nextFromDate, nextToDate, preset) => {
    setAppliedFromDate(nextFromDate);
    setAppliedToDate(nextToDate);
    setActivePreset(preset);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePresetChange = preset => {
    if (preset === 'custom') {
      setActivePreset('custom');
      return;
    }

    const { fromDate: presetFromDate, toDate: presetToDate } = getPresetDateRange(preset);
    setFromDate(presetFromDate);
    setToDate(presetToDate);
    applyDateRange(presetFromDate, presetToDate, preset);
  };

  const handleApplyCustom = () => {
    if (!fromDate || !toDate) return;
    applyDateRange(fromDate, toDate, 'custom');
  };

  const handleClearDateFilter = () => {
    setActivePreset(null);
    setFromDate('');
    setToDate('');
    setAppliedFromDate('');
    setAppliedToDate('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleTypeChange = e => {
    setSelectedType(e.target.value);
    setAppliedType(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const routeMapping = {
    'User Wallet': '/wallet-management/user',
    'Staff Wallet': '/wallet-management/staff',
  };

  const totalRecords = walletdata?.data?.totalDocs ?? 0;
  const transactions = walletdata?.data?.docs ?? [];
  const dueSummaryCards = useMemo(
    () => [
      {
        label: 'Total Wallet Balance',
        value: formatCurrency(dueSummary?.totalWalletBalance),
        hint: 'Combined wallet balance across matched staff',
        accent: { background: '#fff4de', color: '#f05423' },
        icon: <FaWallet />,
      },
      {
        label: 'Total Due Amount',
        value: formatCurrency(dueSummary?.totalDueAmount),
        hint: 'Wallet balance after removing held deposits',
        accent: { background: '#e8f5e9', color: '#2e7d32' },
        icon: <HiOutlineCurrencyRupee />,
      },
      {
        label: 'Total Security Deposit',
        value: formatCurrency(dueSummary?.totalSecurityDeposit),
        hint: 'Security retained only when full threshold is covered',
        accent: { background: '#fce4ec', color: '#c2185b' },
        icon: <HiOutlineCurrencyRupee />,
      },
      {
        label: 'Total Staff',
        value: dueSummary?.totalStaff ?? 0,
        hint: 'Staff included in this summary',
        accent: { background: '#e3f2ff', color: '#1976d2' },
        icon: <HiOutlineCalendarDays />,
      },
      {
        label: 'Security Deposit Per Staff',
        value: formatCurrency(dueSummary?.securityDepositPerStaff),
        hint: 'Configured threshold for each staff wallet',
        accent: { background: '#f3e8ff', color: '#7c3aed' },
        icon: <HiOutlineCurrencyRupee />,
      },
      {
        label: 'Negative Wallet Amount',
        value: formatCurrency(dueSummary?.totalNegativeWalletAmount),
        hint: 'Combined negative wallet balance',
        accent: { background: '#fff8e1', color: '#f9a825' },
        icon: <HiOutlineCurrencyRupee />,
      },
      {
        label: 'Negative Wallet Staff Count',
        value: dueSummary?.negativeWalletStaffCount ?? 0,
        hint: 'Staff members currently below zero',
        accent: { background: '#ffebee', color: '#c62828' },
        icon: <HiOutlineCurrencyRupee />,
      },
    ],
    [dueSummary]
  );

  const getStaffName = staff => {
    if (staff?.fullName) return staff.fullName;
    return [staff?.firstName, staff?.lastName].filter(Boolean).join(' ') || '-';
  };

  return (
    <div className={`${styles.jobmanagementCotainer} ${pageStyles.page}`}>
      <div className={pageStyles.header}>
        <div className={pageStyles.headerLeft}>
          <h6>Wallet Management</h6>
          <p>View staff wallet transactions, withdrawals, and earnings</p>
        </div>
        <div className={pageStyles.headerActions}>
          <div className={pageStyles.searchBar}>
            <IoSearchSharp color="#8A8A8A" size={18} />
            <input
              type="search"
              placeholder="Search by name, email, or wallet ID"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <button
            type="button"
            className={pageStyles.addFundsBtn}
            onClick={() => navigate('/wallet-management/addfunds')}
          >
            <FaPlus color="#FFFFFF" size={14} />
            <p>Add Funds</p>
          </button>
        </div>
      </div>

      <div className={`${styles.jobselectcontainer} ${pageStyles.tabs}`}>
        <div className={styles.jobStatusContainer}>
          {['User Wallet', 'Staff Wallet'].map((item, index) => (
            <Link key={index} to={routeMapping[item]} className={styles.link}>
              <p
                className={
                  item === 'Staff Wallet' ? styles.jobOptionTrueactive : styles.jobOptionnotactive
                }
              >
                {item}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {summaryLoading ? (
        <div className={pageStyles.summaryLoading}>
          <img src={img} alt="Loading summary..." width={80} height={80} />
        </div>
      ) : (
        <div className={`${pageStyles.summaryGrid} ${pageStyles.summaryGridExpanded}`}>
          {dueSummaryCards.map(card => (
            <div key={card.label} className={pageStyles.summaryCard}>
              <div className={pageStyles.summaryCardAccent} style={card.accent}>
                {card.icon}
              </div>
              <div className={pageStyles.summaryCardBody}>
                <p className={pageStyles.summaryCardLabel}>{card.label}</p>
                <p className={pageStyles.summaryCardValue}>{card.value}</p>
                <p className={pageStyles.summaryCardHint}>{card.hint}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={pageStyles.filterPanel}>
        <StaffWalletDateFilters
          activePreset={activePreset}
          fromDate={fromDate}
          toDate={toDate}
          onPresetChange={handlePresetChange}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onApplyCustom={handleApplyCustom}
          onClear={handleClearDateFilter}
          hasActiveFilters={hasActiveDateFilter}
        />
        <div className={pageStyles.typeFilter}>
          <label htmlFor="staff-wallet-type">Transaction Type</label>
          <select id="staff-wallet-type" value={selectedType} onChange={handleTypeChange}>
            {TYPE_OPTIONS.map(option => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={pageStyles.tableSection}>
        <div className={pageStyles.tableSectionHeader}>
          <h6>Staff Wallet Transactions</h6>
          <span className={pageStyles.recordCount}>{totalRecords} transactions</span>
        </div>

        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Staff Member</th>
                <th>Wallet ID</th>
                <th className={pageStyles.alignRight}>Current Wallet</th>
                <th>Status</th>
                <th className={pageStyles.alignRight}>Amount</th>
                <th>Site Name</th>
                <th>Type</th>
                <th>Category</th>
                <th>Description</th>
                <th>Created By</th>
                <th>Payment Mode</th>
                <th>Withdrawal</th>
                <th className={pageStyles.alignRight}>Jobs Done</th>
                <th className={pageStyles.alignRight}>Other Credits</th>
                <th className={pageStyles.alignRight}>Total Withdrawal</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="17" className={pageStyles.loadingCell}>
                    <img src={img} alt="Loading..." width={80} height={80} />
                  </td>
                </tr>
              ) : transactions.length ? (
                transactions.map((item, index) => (
                  <tr key={item?._id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{formatDateTimeP(item?.date) || '-'}</td>
                    <td>
                      <div className={pageStyles.staffCell}>
                        {item?.staff?._id ? (
                          <Link
                            to={`/staff-management/registered/staff-ledger/${item.staff._id}`}
                            className={pageStyles.staffLink}
                          >
                            {getStaffName(item.staff)}
                          </Link>
                        ) : (
                          <span className={pageStyles.staffName}>{getStaffName(item?.staff)}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={pageStyles.idBadge}>{item?.staff?.Id || '-'}</span>
                    </td>
                    <td className={pageStyles.alignRight}>
                      {formatCurrency(item?.currentWallet ?? item?.staff?.wallet)}
                    </td>
                    <td>{item?.status || '-'}</td>
                    <td
                      className={`${pageStyles.alignRight} ${
                        item?.type === 'Credit' ? pageStyles.creditAmount : pageStyles.debitAmount
                      }`}
                    >
                      {item?.type === 'Credit' ? '+' : '-'}
                      {formatCurrency(item?.amount)}
                    </td>
                    <td>{item?.siteName || '-'}</td>
                    <td>
                      <span
                        className={`${pageStyles.typeBadge} ${
                          item?.type === 'Credit' ? pageStyles.typeCredit : pageStyles.typeDebit
                        }`}
                      >
                        {item?.type || '-'}
                      </span>
                    </td>
                    <td>{item?.transactionCategory || '-'}</td>
                    <td>{item?.description || '-'}</td>
                    <td>{item?.createdBy || '-'}</td>
                    <td>{item?.paymentMode || '-'}</td>
                    <td>{item?.isWithdrawal ? 'Yes' : 'No'}</td>
                    <td className={pageStyles.alignRight}>{item?.totalJobsDone ?? 0}</td>
                    <td className={pageStyles.alignRight}>
                      {formatCurrency(item?.totalOtherCredits)}
                    </td>
                    <td className={pageStyles.alignRight}>
                      {formatCurrency(item?.totalWithdrawal)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="17" className={pageStyles.emptyCell}>
                    <div className={pageStyles.emptyState}>
                      <p>No wallet transactions found</p>
                      <span>Try adjusting your search, date, or type filter</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={pageStyles.paginationWrap}>
          <Pagination
            limit={pagination.limit}
            totalPages={pagination.totalPages}
            page={pagination.page}
            hasPrevPage={pagination.hasPrevPage}
            hasNextPage={pagination.hasNextPage}
            onPageChange={newPage => setPagination({ ...pagination, page: newPage })}
          />
        </div>
      </div>
    </div>
  );
};

export default HOC(StaffWalletManagement);
